import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabase';

export interface SocialMediaLink {
  id: string;
  platform: string;
  platform_name: string;
  url: string;
  icon_name: string;
  display_order: number;
  is_active: boolean;
}

export interface SocialMediaSettings {
  show_social_links_header: boolean;
  show_social_links_footer: boolean;
}

const defaultSocialSettings: SocialMediaSettings = {
  show_social_links_header: false,
  show_social_links_footer: false
};

export const useSocialMediaLinks = () => {
  const [socialLinks, setSocialLinks] = useState<SocialMediaLink[]>([]);
  const [socialSettings, setSocialSettings] = useState<SocialMediaSettings>(defaultSocialSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const fetchSocialLinks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch social media links
      const { data: linksData, error: linksError } = await supabase
        .from('social_media_links')
        .select('*')
        .order('display_order', { ascending: true });

      if (linksError) {
        throw linksError;
      }

      // Fetch social media settings
      const { data: settingsData, error: settingsError } = await supabase
        .from('settings')
        .select('*')
        .eq('category', 'social')
        .eq('is_active', true);

      if (settingsError) {
        throw settingsError;
      }

      // Process settings
      const settings: Partial<SocialMediaSettings> = {};
      if (settingsData && settingsData.length > 0) {
        settingsData.forEach((setting: any) => {
          if (setting.setting_key in defaultSocialSettings) {
            (settings as any)[setting.setting_key] = setting.setting_value === 'true';
          }
        });
      }

      setSocialLinks(linksData || []);
      const finalSettings = { ...defaultSocialSettings, ...settings };
      setSocialSettings(finalSettings);
      setIsInitialized(true);
    } catch (err) {
      console.error('Error fetching social media links:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch social media links');
      setSocialLinks([]);
      setSocialSettings(defaultSocialSettings);
      setIsInitialized(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateSocialLink = useCallback(async (id: string, updates: Partial<SocialMediaLink>) => {
    try {
      setError(null);

      const { error: updateError } = await supabase
        .from('social_media_links')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (updateError) {
        throw updateError;
      }

      // Update local state
      setSocialLinks(prev => 
        prev.map(link => 
          link.id === id ? { ...link, ...updates } : link
        )
      );

      return { success: true };
    } catch (err) {
      console.error('Error updating social media link:', err);
      setError(err instanceof Error ? err.message : 'Failed to update social media link');
      return { success: false, error: err instanceof Error ? err.message : 'Failed to update social media link' };
    }
  }, []);

  const addSocialLink = useCallback(async (newLink: Omit<SocialMediaLink, 'id'>) => {
    try {
      setError(null);

      const { data, error: insertError } = await supabase
        .from('social_media_links')
        .insert([{
          ...newLink,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (insertError) {
        throw insertError;
      }

      // Update local state
      setSocialLinks(prev => [...prev, data].sort((a, b) => a.display_order - b.display_order));

      return { success: true, data };
    } catch (err) {
      console.error('Error adding social media link:', err);
      setError(err instanceof Error ? err.message : 'Failed to add social media link');
      return { success: false, error: err instanceof Error ? err.message : 'Failed to add social media link' };
    }
  }, []);

  const deleteSocialLink = useCallback(async (id: string) => {
    try {
      setError(null);

      const { error: deleteError } = await supabase
        .from('social_media_links')
        .delete()
        .eq('id', id);

      if (deleteError) {
        throw deleteError;
      }

      // Update local state
      setSocialLinks(prev => prev.filter(link => link.id !== id));

      return { success: true };
    } catch (err) {
      console.error('Error deleting social media link:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete social media link');
      return { success: false, error: err instanceof Error ? err.message : 'Failed to delete social media link' };
    }
  }, []);

  const updateSocialSettings = useCallback(async (newSettings: Partial<SocialMediaSettings>) => {
    try {
      setError(null);

      const updates = Object.entries(newSettings).map(([key, value]) => ({
        setting_key: key,
        setting_value: String(value),
        setting_type: 'boolean',
        category: 'social',
        description: `Social media ${key.replace(/_/g, ' ')}`,
        is_active: true,
        updated_at: new Date().toISOString()
      }));

      const { error: updateError } = await supabase
        .from('settings')
        .upsert(updates, { onConflict: 'setting_key' });

      if (updateError) {
        throw updateError;
      }

      // Update local state
      setSocialSettings(prev => ({ ...prev, ...newSettings }));

      return { success: true };
    } catch (err) {
      console.error('Error updating social media settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to update social media settings');
      return { success: false, error: err instanceof Error ? err.message : 'Failed to update social media settings' };
    }
  }, []);

  useEffect(() => {
    fetchSocialLinks();

    // Set up real-time subscriptions for social media links
    const socialLinksSubscription = supabase
      .channel('social_media_links_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'social_media_links'
        },
        (payload) => {
          // Only refetch if we're initialized to avoid infinite loops
          if (isInitialized) {
            fetchSocialLinks();
          }
        }
      )
      .subscribe();

    // Set up real-time subscriptions for social media settings
    const socialSettingsSubscription = supabase
      .channel('social_settings_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'settings',
          filter: 'category=eq.social'
        },
        (payload) => {
          // Only refetch if we're initialized to avoid infinite loops
          if (isInitialized) {
            fetchSocialLinks();
          }
        }
      )
      .subscribe();

    // Cleanup subscriptions on unmount
    return () => {
      supabase.removeChannel(socialLinksSubscription);
      supabase.removeChannel(socialSettingsSubscription);
    };
  }, [fetchSocialLinks, isInitialized]);

  // Get active social links for display - only include links with valid URLs
  const activeSocialLinks = socialLinks.filter(link =>
    link.is_active &&
    link.url &&
    link.url.trim() !== '' &&
    link.url !== '#' &&
    (link.url.startsWith('http://') || link.url.startsWith('https://'))
  );

  return {
    socialLinks,
    activeSocialLinks,
    socialSettings,
    loading,
    error,
    isInitialized,
    updateSocialLink,
    addSocialLink,
    deleteSocialLink,
    updateSocialSettings,
    refetch: fetchSocialLinks
  };
};